import { OrganizationType } from '../models/Organization';
import { SubscriptionPlanSummary } from '../models/Subscription';

const ONBOARDING_DISPLAY_NAME_KEY = 'subscription_onboarding_display_name';
const ONBOARDING_ORGANIZATION_NAME_KEY = 'subscription_onboarding_organization_name';
const ONBOARDING_ACTIVE_KEY = 'subscription_onboarding_active';
const ONBOARDING_NEXT_KEY = 'subscription_onboarding_next';
const ONBOARDING_ACCESS_TOKEN_KEY = 'subscription_onboarding_access_token';

/** Fullscreen signup plan picker (outside host dashboard). */
export function buildSubscriptionOnboardingPath(options?: {
  next?: string;
}): string {
  const params = new URLSearchParams();
  const next = options?.next?.trim();
  if (next && next.startsWith('/') && !next.startsWith('//')) {
    params.set('next', next);
  }
  const query = params.toString();
  return query ? `/onboarding/plan?${query}` : '/onboarding/plan';
}

export function resolveOnboardingContinuePath(
  next: string | null | undefined,
  fallback = '/host/organization',
): string {
  const candidate = next?.trim();
  if (candidate && candidate.startsWith('/') && !candidate.startsWith('//')) {
    return candidate;
  }
  return fallback;
}

export function cacheOrganizationType(type: OrganizationType): void {
  localStorage.setItem('organization_type', type);
}

export function clearCachedOrganizationType(): void {
  localStorage.removeItem('organization_type');
}

/** Default org name when signup only collects a username. */
export function defaultOrganizationNameFromUsername(username: string): string {
  const name = username.trim();
  if (!name) return 'My organization';
  return `${name}'s organization`;
}

export function storeOnboardingDisplayName(name: string): void {
  sessionStorage.setItem(ONBOARDING_DISPLAY_NAME_KEY, name.trim());
}

export function readOnboardingDisplayName(): string | null {
  const value = sessionStorage.getItem(ONBOARDING_DISPLAY_NAME_KEY)?.trim();
  return value || null;
}

export function clearOnboardingDisplayName(): void {
  sessionStorage.removeItem(ONBOARDING_DISPLAY_NAME_KEY);
}

export function storeOnboardingOrganizationName(name: string): void {
  const trimmed = name.trim();
  if (!trimmed) {
    sessionStorage.removeItem(ONBOARDING_ORGANIZATION_NAME_KEY);
    return;
  }
  sessionStorage.setItem(ONBOARDING_ORGANIZATION_NAME_KEY, trimmed);
}

export function readOnboardingOrganizationName(): string | null {
  const value = sessionStorage.getItem(ONBOARDING_ORGANIZATION_NAME_KEY)?.trim();
  return value || null;
}

export function clearOnboardingOrganizationName(): void {
  sessionStorage.removeItem(ONBOARDING_ORGANIZATION_NAME_KEY);
}

/** Prefer optional org name from complete-profile; otherwise `{username}'s organization`. */
export function resolveOnboardingOrganizationName(displayName: string): string {
  return readOnboardingOrganizationName() ?? defaultOrganizationNameFromUsername(displayName);
}

export function storeOnboardingAccessToken(accessToken: string): void {
  sessionStorage.setItem(ONBOARDING_ACCESS_TOKEN_KEY, accessToken);
}

export function readOnboardingAccessToken(): string | null {
  const value = sessionStorage.getItem(ONBOARDING_ACCESS_TOKEN_KEY)?.trim();
  return value || localStorage.getItem('access_token');
}

export function clearOnboardingAccessToken(): void {
  sessionStorage.removeItem(ONBOARDING_ACCESS_TOKEN_KEY);
}

/** Survives Stripe Checkout round-trips so return to /host/subscription can finish signup. */
export function markSubscriptionOnboardingActive(next?: string | null): void {
  sessionStorage.setItem(ONBOARDING_ACTIVE_KEY, '1');
  const safeNext = next?.trim();
  if (safeNext && safeNext.startsWith('/') && !safeNext.startsWith('//')) {
    sessionStorage.setItem(ONBOARDING_NEXT_KEY, safeNext);
  }
}

/**
 * Prefill onboarding session for an existing account (e.g. pricing → Start Free Trial).
 * Skips `/complete-profile` when the user already has a display name.
 */
export function beginSubscriptionOnboardingSession(options: {
  accessToken: string;
  displayName: string;
  next?: string | null;
}): boolean {
  const accessToken = options.accessToken.trim();
  const displayName = options.displayName.trim();
  if (!accessToken || !displayName) {
    return false;
  }
  storeOnboardingAccessToken(accessToken);
  storeOnboardingDisplayName(displayName);
  markSubscriptionOnboardingActive(options.next);
  return true;
}

export function isSubscriptionOnboardingPath(path: string | null | undefined): boolean {
  const candidate = path?.trim();
  if (!candidate) return false;
  try {
    const url = new URL(candidate, window.location.origin);
    return url.pathname === '/onboarding/plan';
  } catch {
    return candidate === '/onboarding/plan' || candidate.startsWith('/onboarding/plan?');
  }
}

export function isSubscriptionOnboardingActive(): boolean {
  return sessionStorage.getItem(ONBOARDING_ACTIVE_KEY) === '1';
}

export function readSubscriptionOnboardingNext(): string | null {
  const value = sessionStorage.getItem(ONBOARDING_NEXT_KEY)?.trim();
  return value || null;
}

export function clearSubscriptionOnboardingActive(): void {
  sessionStorage.removeItem(ONBOARDING_ACTIVE_KEY);
  sessionStorage.removeItem(ONBOARDING_NEXT_KEY);
  clearOnboardingAccessToken();
  clearOnboardingOrganizationName();
}

/**
 * Create host or tutor org for the selected plan during signup.
 * Call before Stripe checkout. Returns false if required session data is missing.
 */
export async function ensureProviderProfileForPlan(options: {
  plan: SubscriptionPlanSummary;
  completeHostProfile: (request: {
    accessToken: string;
    name: string;
    organizationName: string;
    organizationType?: OrganizationType.REHEARSAL_STUDIO | OrganizationType.MUSIC_SCHOOL;
  }) => Promise<void>;
  completeTutorProfile: (request: {
    accessToken: string;
    name: string;
  }) => Promise<void>;
  refreshSession: () => Promise<boolean>;
  getRoles: () => string[];
}): Promise<{ ok: true } | { ok: false; reason: 'missing_session' | 'refresh_failed' | 'role_failed' }> {
  const accessToken = readOnboardingAccessToken();
  const displayName = readOnboardingDisplayName();
  if (!accessToken || !displayName) {
    return { ok: false, reason: 'missing_session' };
  }

  const organizationName = resolveOnboardingOrganizationName(displayName);
  const roles = options.getRoles();
  const alreadyProvider = roles.includes('HOST') || roles.includes('TUTOR');

  if (alreadyProvider) {
    if (
      options.plan.organizationType === OrganizationType.INDIVIDUAL_TUTOR &&
      !roles.includes('TUTOR')
    ) {
      await options.completeTutorProfile({ accessToken, name: displayName });
      const refreshed = await options.refreshSession();
      if (!refreshed || !options.getRoles().includes('TUTOR')) {
        return { ok: false, reason: refreshed ? 'role_failed' : 'refresh_failed' };
      }
      cacheOrganizationType(OrganizationType.INDIVIDUAL_TUTOR);
    }
    return { ok: true };
  }

  if (options.plan.organizationType === OrganizationType.INDIVIDUAL_TUTOR) {
    await options.completeTutorProfile({
      accessToken,
      name: displayName,
    });
  } else if (
    options.plan.organizationType === OrganizationType.REHEARSAL_STUDIO ||
    options.plan.organizationType === OrganizationType.MUSIC_SCHOOL
  ) {
    await options.completeHostProfile({
      accessToken,
      name: displayName,
      organizationName,
      organizationType: options.plan.organizationType,
    });
  } else {
    await options.completeHostProfile({
      accessToken,
      name: displayName,
      organizationName,
    });
  }

  const refreshed = await options.refreshSession();
  if (!refreshed) {
    return { ok: false, reason: 'refresh_failed' };
  }
  const nextRoles = options.getRoles();
  if (options.plan.organizationType === OrganizationType.INDIVIDUAL_TUTOR) {
    if (!nextRoles.includes('TUTOR') || !nextRoles.includes('HOST')) {
      return { ok: false, reason: 'role_failed' };
    }
    cacheOrganizationType(OrganizationType.INDIVIDUAL_TUTOR);
  } else if (!nextRoles.includes('HOST')) {
    return { ok: false, reason: 'role_failed' };
  } else {
    cacheOrganizationType(options.plan.organizationType);
  }

  return { ok: true };
}
